
import angular from "angular";
import { RuntimeContext } from "../../Environments";
import { IResponse } from "../model/response";
import { IResponseEvent } from "../model/response-event";
import { IRetryParameter } from "../model/retry-parameter";
import { UrlUtil } from "../utils/url-util";
import { ILogger } from "./logger";
import { IRestfulConfigMap } from "./restful-config-map";
import { IRestfulAction } from "./restfult-action";

export class HttpHandler {

    private SERVICE_CONFIGS: IRestfulConfigMap;

    private RETRY_MAX_TIME: number;

    private RETRY_TIME_SPAN: number;

    private DEFAULT_HEADERS: angular.IHttpRequestConfigHeaders;

    constructor(
        private $http: angular.IHttpService,
        private $q: angular.IQService,
        private $timeout: angular.ITimeoutService,
        private runtimeContext: RuntimeContext,
        private $cacheFactory: angular.ICacheFactoryService,
        private $logger?: ILogger
    ) {
        this.promiseSuccess = this.promiseSuccess.bind(this);
        this.promiseError = this.promiseError.bind(this);
        this.downloadFileByBlob = this.downloadFileByBlob.bind(this);
        this.readJsonByBlob = this.readJsonByBlob.bind(this);
        this.retryPromise = this.retryPromise.bind(this);
        this.httpRetry.bind(this);
    }

    public init(serviceConfigs: IRestfulConfigMap, retryMaxTime: number, retryTimeSpan: number, defaultHeaders: angular.IHttpRequestConfigHeaders) {
        this.SERVICE_CONFIGS = serviceConfigs;
        this.RETRY_MAX_TIME = retryMaxTime;
        this.RETRY_TIME_SPAN = retryTimeSpan
        this.DEFAULT_HEADERS = defaultHeaders;
    }

    private sendError(moduleName: string, e: any) {
        if (this.$logger != undefined) {
            this.$logger.error(moduleName, e);
        }
    }

    /**
       * @function restService.promiseSuccess
       * @description 调用promise成功返回时处理
       * 
       * @param {*} event 
       * @param {*} deferred 
       */
    private promiseSuccess<T>(event: IResponseEvent<T>, deferred: angular.IDeferred<IResponse<T>>): void {
        (function (e: IResponseEvent<T>) {
            if (e.success || e.Success || (e.data && (e.data.success || e.data.Success))) {
                deferred.resolve(e.data || e);
            } else if (e.config.responseType == "blob") {
                deferred.resolve(e.data || e);
            } else {
                // 使用sentry处理200类型错误
                this.sendError("RESTFUL_RESOLVE", e);
                // Sentry.withScope(this.sentryService.getScope("RESTFUL_RESOLVE", function (scope) {
                //     Sentry.captureException(e);
                // }));
                deferred.reject(e);
            }
        }).bind(this)(event);
    }

    /**
     * @function restService.promiseError
     * @description 调用promise失败时处理
     * 
     * @param {*} event 
     * @param {*} deferred 
     */
    private promiseError<T>(event: IResponseEvent<T>, deferred: angular.IDeferred<IResponse<T>>): void {
        // // 抓取请求失败错误手动发送sentry
        this.sendError("RESTFUL_REJECT", event);
        // Sentry.withScope(this.sentryService.getScope("RESTFUL_REJECT", function (scope) {
        //     Sentry.captureException(event);
        // }));

        (function (e) {
            deferred.reject(e);
        })(event);
    }

    /**
     * 通过Blob下载文件
     * @param {*} event 
     */
    private downloadFileByBlob(event: angular.IHttpResponse<any>) {
        let blob = new Blob([event.data], {
            type: event.data.type
        });
        let reader = new FileReader();
        reader.readAsBinaryString(blob);

        let downloadElement = document.createElement('a');
        downloadElement.href = window.URL.createObjectURL(blob);

        let fileName = "";
        //服务器返回了指定文件名
        if (event.headers("content-disposition") && /filename=([\w-.]*)/i.test(event.headers("content-disposition"))) {
            let names = event.headers("content-disposition").match(/filename=([\w-.]*)/i);

            if (names && names.length > 1) {
                fileName = names[1];
            }
        }

        //如果服务器没有指定文件名，则生成时间有关的随机名称
        if (fileName == "") {
            let now = new Date();
            let fileFormat = "txt";
            let formats = event.headers("content-type").match(/(application|image)\/([\w|\w\+\w]*)(;|$)/i);
            if (formats && formats.length > 2) {
                fileFormat = formats[2];
            }

            // if (fileFormat == "octet-stream") {
            //     event.headers("content-disposition") == "image/svg+xml";
            //     fileFormat = "svg";
            // }

            if (fileFormat == "svg+xml") {
                fileFormat = "svg";
            }

            fileName = now.getFullYear().toString() + "-" + (now.getMonth() + 1).toString() + "-" + now.getDate() + "_" + Math.random() * 10000 + "." + fileFormat;
        }

        downloadElement.download = fileName;
        document.body.appendChild(downloadElement);
        downloadElement.click();
        downloadElement.remove();
        window.URL.revokeObjectURL(downloadElement.href);
    }

    /**
     * 通过Blob读取JSON对象
     * @param {*} event 
     */
    private readJsonByBlob(event: angular.IHttpResponse<any>, callback?: Function) {
        let blob = new Blob([event.data], {
            type: event.data.type
        });
        let reader = new FileReader();
        reader.readAsBinaryString(blob);

        reader.onload = function () {
            //是否需要retry
            let result = reader.result as string;
            let resultToJson = JSON.parse(result);
            callback && callback(resultToJson);
        }
    }

    /**
     * @function restService.promiseRetry
     * @description 调用promise成功返回时处理
     * @param {*} obj 
     * @param {*} excuteTimes 
     * @param {*} event 
     * @param {*} deferred 
     * @param {*} retryFunc 
     */
    private retryPromise<T>(obj: angular.IRequestConfig, event: IResponseEvent<T>, deferred: angular.IDeferred<IResponse<T>>, retryParameter: IRetryParameter) {
        if (event.success || event.Success || (event.data && (event.data.success || event.data.Success))) {
            //需要重试
            if (retryParameter.needRetry || event.needRetry || event.data.needRetry) {
                //未达到重试次数上限
                if (retryParameter.retryMaxtime && retryParameter.retryMaxtime >= retryParameter.excuteTimes) {
                    this.$timeout(function () {
                        retryParameter.excuteTimes + 1;
                        retryParameter.retryFunc(obj, deferred, retryParameter);
                    }, retryParameter.retryTimeSpan)
                } else {
                    //达到重试次数上限
                    this.promiseError(event, deferred);
                    // 通知用户并询问是否重试
                }
            } else {
                this.promiseSuccess(event, deferred);
            }
        } else {
            // // 使用sentry处理200类型错误      
            this.sendError("RESTFUL_RESOLVE", event);
            // Sentry.withScope(this.sentryService.getScope("RESTFUL_RESOLVE", function (scope) {
            //     Sentry.captureException(event);
            // }));
            this.promiseError(event, deferred);
            // 通知用户,系统错误
        }
    }

    /**
     * @function restService.httpRetry
     * @description 生成http请求的Promise
     * 
     * @param {*} obj 
     * @param {Number} excuteTimes
     * @param {*} deferred 
     * @param {*} successFunc 
     * @param {*} errorFunc 
     */
    private httpRetry<T>(httpConfig: angular.IRequestConfig, deferred: angular.IDeferred<IResponse<T>>, retryParameter: IRetryParameter) {
        this.$http(httpConfig).then(
            //成功返回处理函数
            function (event: IResponseEvent<T>) {
                if (event && event.config && event.config.responseType === "blob") {
                    if (/(application\/json)/i.test(event.headers("content-type"))) {
                        this.readJsonByBlob(event, (data) => {
                            event.data = data;
                            this.retryPromise(httpConfig, event, deferred, retryParameter);
                        });
                    } else {
                        this.downloadFileByBlob(event);
                        this.promiseSuccess(event, deferred);
                    }
                } else {
                    this.retryPromise(httpConfig, event, deferred, retryParameter);
                }
            }.bind(this),
            //调用失败时的处理函数
            function (event) {
                this.promiseError(event, deferred);
            }.bind(this)
        );
    }

    private getCacheFactory(serviceName: string): angular.ICacheObject {
        if (this.$cacheFactory.get(serviceName)) {
            return this.$cacheFactory.get(serviceName);
        } else {
            return this.$cacheFactory(serviceName);
        }
    }

    /**
     * http请求
     * @function restService.http
     * @param serviceName 
     * @param action 
     * @param config 
     * @returns 
     */
    public http<T>(serviceName: string, action: string): angular.IPromise<IResponse<T>>;
    public http<T, S>(serviceName: string, action: string, config: { parameter: S, headers?: angular.IHttpRequestConfigHeaders }): angular.IPromise<IResponse<T>>;
    public http<T, S>(serviceName: string, action: string, config: { data: S, headers?: angular.IHttpRequestConfigHeaders }): angular.IPromise<IResponse<T>>;
    public http<T, R, S>(serviceName: string, action: string, config?: { parameter?: S, data?: R, headers?: angular.IHttpRequestConfigHeaders }): angular.IPromise<IResponse<T>>;
    public http<T, R, S>(serviceName: string, action: string, config?: { parameter?: S, data?: R, headers?: angular.IHttpRequestConfigHeaders }): angular.IPromise<IResponse<T>> {

        //http请求的配置对象
        let cfg = this.SERVICE_CONFIGS[serviceName.toLowerCase()];
        if (!cfg) {
            throw new Error("missing service config:" + serviceName);
        }

        if (!cfg.actions) {
            throw new Error("missing actions config in " + serviceName);
        }

        let actionCfg: IRestfulAction | undefined;

        for (let k in Object.assign(cfg.actions)) {
            if (action.toLowerCase() == k.toLowerCase()) {
                actionCfg = cfg.actions[k];
            }
        }

        if (!actionCfg) {
            throw new Error("missing action config '" + action + "' in " + serviceName);
        }

        //构建URL参数
        let path =  this.runtimeContext.builder.useTestVersion && actionCfg.testPath ? actionCfg.testPath : actionCfg.path;

        // actionCfg.paramDefaults:URL参数
        if (actionCfg.paramDefaults) {
            if (config) {
                if (config.parameter) {
                    path = UrlUtil.setUrlParams(path, actionCfg.paramDefaults, config.parameter);
                } else if (config.data) {
                    path = UrlUtil.setUrlParams(path, actionCfg.paramDefaults, config.data);
                }
            }
        }

        let url = UrlUtil.joinUrl([cfg.appPath, path]);

        //构建$http参数
        let httpCfg: angular.IRequestConfig = {
            url: url,
            method: actionCfg.method,
            // headers: this.DEFAULT_HEADERS
            headers: angular.copy(this.DEFAULT_HEADERS)
        };

        if (config) {
            if (config.data) {
                httpCfg.data = config.data;
            }
            if (config.headers) {
                httpCfg.headers = angular.extend({}, httpCfg.headers, config.headers);
            }
        }

        if (actionCfg.requestConfig) {
            angular.merge(httpCfg, actionCfg.requestConfig);
        }

        //是否需要校验
        let isAuth = actionCfg.isAuth;

        if (isAuth === undefined) {
            isAuth = (cfg.isAuth === undefined ? true : cfg.isAuth);
        }

        //是否需要认证
        // if (isAuth && this.runtimeContext.rest.partnerToken) {
        //     angular.merge(httpCfg, {
        //         "headers": {
        //             "Authorization": this.runtimeContext.rest.partnerToken.token_type + " " + this.runtimeContext.rest.partnerToken.access_token
        //         }
        //     });
        // }

        let deferred = this.$q.defer<IResponse<T>>();

        // TODO: cache与retry同时存在的情况
        /**
         * @description: http cache缓存
         */
        // let cache = actionCfg.cache;
        // if (cache != undefined || cache != null) {
        //     let cfgServiceName = serviceName.toLowerCase();

        //     if (!cache.cacheId) {
        //         throw new Error("missing cahce config in " + serviceName);
        //     }

        //     let { cacheId } = cache;
        //     let needParameterReplace: boolean = (/^@/).test(cacheId);
        //     let cacheFactory: angular.ICacheObject = this.getCacheFactory(cfgServiceName);

        //     ConsoleLogger.setLogger(false);
        //     // cacheId替换
        //     // (只有get请求才做缓存)
        //     if ((httpCfg.method).toLowerCase() == "get" && needParameterReplace) {
        //         if (config && config.parameter) {
        //             for (let key in Object.assign(config.parameter as {})) {
        //                 if (Object.prototype.hasOwnProperty.call(config.parameter, key)) {
        //                     const value = config.parameter[key];

        //                     if (RegExp("@" + key).test(cacheId)) {
        //                         cacheId = cache.combineKey
        //                             ? cacheId.replace("@" + key, "$" + cache.combineKey + "$" + value)
        //                             : cacheId.replace("@" + key, key + ":" + value + "$");
        //                     }
        //                 }
        //             }
        //         }
        //     }

        //     if (cacheFactory.get(cacheId)) { // cacheFactory已有缓存id
        //         deferred.resolve(cacheFactory.get(cacheId));
        //     } else { // cacheFactory未有缓存id
        //         this.$http(httpCfg).then((event: any) => {
        //             if (event.success || event.Success ||
        //                 (event.data && (event.data.success || event.data.Success))
        //             ) {
        //                 cacheFactory.put(cacheId, event.data);
        //                 this.promiseSuccess(event, deferred);
        //             } else {
        //                 this.promiseError(event, deferred);
        //             }
        //         }, (event: IResponseEvent<T>) => {
        //             // 调用失败时的处理函数
        //             this.promiseError(event, deferred);
        //         })
        //     }

        //     return deferred.promise;
        // }

        let retryParameter: IRetryParameter = {
            excuteTimes: 0,
            retryFunc: this.httpRetry.bind(this)
        };

        if (actionCfg.needRetry) {
            retryParameter.needRetry = true;
            retryParameter.retryMaxtime = actionCfg.retryMaxtime || this.RETRY_MAX_TIME;
            retryParameter.retryTimeSpan = actionCfg.retryTimeSpan || this.RETRY_TIME_SPAN;
        } else {
            retryParameter.retryMaxtime = this.RETRY_MAX_TIME;
            retryParameter.retryTimeSpan = this.RETRY_TIME_SPAN;
        }

        this.httpRetry(httpCfg, deferred, retryParameter);

        return deferred.promise;
    }
}